import { useState } from "react";
import { t } from "i18next";
import type { NavigateFunction } from "react-router-dom";
import { Ban, CheckCircle2, Eye, ShieldCheck, XCircle } from "lucide-react";

import { RowActionsMenu } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useApproveWalletKyc } from "@/features/wallet/api/use-approve-wallet-kyc";
import { useSetWalletStatus } from "@/features/wallet/api/use-set-wallet-status";
import { CloseWalletConfirmDialog } from "@/features/wallet/components/wallets/close-wallet-confirm-dialog";
import type { WalletRow } from "@/features/wallet/components/wallets/wallets-columns";
import type { WalletStatus } from "@/features/shared/api/enums";

/**
 * Statuts cibles proposés selon le statut courant (guide §6.3 : cibles
 * admises ACTIVE, SUSPENDED, CLOSED). Un portefeuille PENDING_KYC ne passe
 * en ACTIVE que via `kyc-approval` (étape 6), jamais via /status.
 */
type ActionableWalletStatus = "ACTIVE" | "SUSPENDED" | "CLOSED";

const nextStatuses = (status?: WalletStatus): ActionableWalletStatus[] => {
  switch (status) {
    case "ACTIVE":
      return ["SUSPENDED", "CLOSED"];
    case "SUSPENDED":
      return ["ACTIVE", "CLOSED"];
    case "PENDING_KYC":
    case "CLOSED":
    default:
      return [];
  }
};

const STATUS_ACTION_LABEL: Record<ActionableWalletStatus, string> = {
  ACTIVE: "wallet.wallets.actions.activate",
  SUSPENDED: "wallet.wallets.actions.suspend",
  CLOSED: "wallet.wallets.actions.close",
};

const STATUS_ACTION_ICON: Record<ActionableWalletStatus, typeof Ban> = {
  ACTIVE: CheckCircle2,
  SUSPENDED: Ban,
  CLOSED: XCircle,
};

/**
 * Menu d'actions de ligne : voir le détail, changement de statut et
 * approbation KYC (orchestration back-office, étape 6 — Kafka non câblé).
 * Le niveau appliqué au déblocage est le niveau courant du portefeuille.
 */
export function WalletRowActions({
  wallet,
  navigate,
}: {
  wallet: WalletRow;
  /** Fourni par la fabrique de colonnes de `DataTableClar`. */
  navigate: NavigateFunction;
}) {
  const setWalletStatus = useSetWalletStatus();
  const approveKyc = useApproveWalletKyc(wallet.id);
  const [closeOpen, setCloseOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState<
    Exclude<ActionableWalletStatus, "CLOSED"> | null
  >(null);
  const [kycOpen, setKycOpen] = useState(false);

  const applyStatus = (status: ActionableWalletStatus) =>
    setWalletStatus.mutate(
      { walletId: wallet.id, status },
      {
        onSuccess: () => {
          notifSilence(t("wallet.wallets.feedback.statusUpdated"));
          if (status === "CLOSED") setCloseOpen(false);
          else setStatusTarget(null);
        },
        onError: (error) => notifSilenceError(error.message),
      }
    );

  const statusActions = nextStatuses(wallet.status).map((status) => ({
    id: `set-status-${status}`,
    label: t(STATUS_ACTION_LABEL[status]),
    icon: STATUS_ACTION_ICON[status],
    variant:
      status === "SUSPENDED" || status === "CLOSED"
        ? ("destructive" as const)
        : ("default" as const),
    onClick: () =>
      status === "CLOSED" ? setCloseOpen(true) : setStatusTarget(status),
  }));

  return (
    <>
      <RowActionsMenu
        actions={[
          {
            id: "view",
            label: t("wallet.wallets.actions.view"),
            icon: Eye,
            onClick: () => navigate(`/app/wallet/${wallet.id}`),
          },
          ...statusActions,
          ...(wallet.status === "PENDING_KYC"
            ? [
                {
                  id: "kyc-approval",
                  label: t("wallet.wallets.actions.approveKyc"),
                  icon: ShieldCheck,
                  onClick: () => setKycOpen(true),
                },
              ]
            : []),
        ]}
      />
      <CloseWalletConfirmDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        walletId={wallet.id}
        walletNumber={wallet.walletNumber}
        pending={
          setWalletStatus.isPending &&
          setWalletStatus.variables?.status === "CLOSED"
        }
        onConfirm={() => applyStatus("CLOSED")}
      />
      <MutationConfirmPreview
        open={statusTarget !== null}
        onOpenChange={(open) => {
          if (!open) setStatusTarget(null);
        }}
        data={{
          id: wallet.id,
          walletNumber: wallet.walletNumber,
          currentStatus: wallet.status,
          nextStatus: statusTarget,
        }}
        title={t("common.mutationPreview.statusTitle")}
        description={t("common.mutationPreview.statusDescription")}
        confirmLabel={
          statusTarget ? t(STATUS_ACTION_LABEL[statusTarget]) : undefined
        }
        t={t}
        pending={setWalletStatus.isPending}
        onConfirm={() => {
          if (statusTarget) applyStatus(statusTarget);
        }}
      />
      <MutationConfirmPreview
        open={kycOpen}
        onOpenChange={setKycOpen}
        data={{
          id: wallet.id,
          walletNumber: wallet.walletNumber,
          kycLevel: wallet.kycLevel ?? "TIER1",
          status: wallet.status,
        }}
        title={t("common.mutationPreview.title")}
        description={t("common.mutationPreview.description")}
        confirmLabel={t("wallet.wallets.actions.approveKyc")}
        t={t}
        pending={approveKyc.isPending}
        onConfirm={() =>
          approveKyc.mutate(
            { kycLevel: wallet.kycLevel ?? "TIER1" },
            {
              onSuccess: () => {
                notifSilence(t("wallet.wallets.feedback.kycApproved"));
                setKycOpen(false);
              },
              onError: (error) => notifSilenceError(error.message),
            }
          )
        }
      />
    </>
  );
}
